import { memo, useMemo } from 'react';
import { StanDropdownMenu } from '@/components/core-components/StanDropdown/Menu/StanDropdownMenu';
import { DropdownProvider } from '@/components/core-components/StanDropdown/DropdownProvider';
import { AUTOMATION_ACTIONS } from '../constants/actions';
import type { Automation } from '../types/automation.types';
import type { DropdownMenuOption } from '@/components/core-components/StanDropdown/StanDropdown.types';
import { createIconElement } from '@/lib';

import PublishIcon from '@/assets/icons/ic-publish.svg';
import HiddenIcon from '@/assets/icons/ic-hidden.svg';
import TrashIcon from '@/assets/icons/ic-trash.svg';
import MoreVerticalIcon from '@/assets/icons/ic-more-vertical.svg';

interface AutomationActionsMenuProps {
    automation: Automation;
    onAction: (action: string) => void;
}

export const AutomationActionsMenu = memo<AutomationActionsMenuProps>(({ automation, onAction }) => {
    const dropdownOptions = useMemo((): DropdownMenuOption[] => {
        const options: DropdownMenuOption[] = [];
        const isUnsaved = automation.automation_id === undefined;

        // Publish/Unpublish based on status (hide for unsaved automations)
        if (!isUnsaved) {
            if (automation.status === 'draft') {
                options.push({ label: 'Publish', value: AUTOMATION_ACTIONS.PUBLISH, icon: PublishIcon });
            } else if (automation.status === 'published') {
                options.push({ label: 'Unpublish', value: AUTOMATION_ACTIONS.UNPUBLISH, icon: HiddenIcon });
            }
        }

        // Delete option (add divider only if there are other options)
        if (options.length > 0) {
            options.push({ label: '', value: `divider-${automation.automation_id}`, type: 'divider' });
        }
        options.push({
            label: 'Delete',
            value: AUTOMATION_ACTIONS.DELETE,
            icon: TrashIcon,
            type: 'destructive',
        });

        return options;
    }, [automation.status, automation.automation_id]);

    return (
        <DropdownProvider
            dropdownId={`automation-menu-${automation.automation_id}`}
            dropdownName={`automation-menu-${automation.automation_id}`}
        >
            <StanDropdownMenu
                id={`automation-menu-${automation.automation_id}`}
                name={`automation-menu-${automation.automation_id}`}
                options={dropdownOptions}
                placement='bottom-end'
                onSelect={value => onAction(value.value)}
                currentValue={null}
                minWidth={176}
            >
                <button className='text-gray hover:text-dark hover:bg-neutral-hover/50 flex size-8 cursor-pointer items-center justify-center rounded-md transition'>
                    {createIconElement(MoreVerticalIcon)}
                </button>
            </StanDropdownMenu>
        </DropdownProvider>
    );
});

AutomationActionsMenu.displayName = 'AutomationActionsMenu';
